import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { X, CheckCircle2, User, Layers, Search, AlertCircle, Save, Loader2, Check, UserMinus, Clock, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getApiUrl } from "../config/api";
import "../styles/DashboardLayout.css";
import "../styles/UtopiaPages.css";

interface ClassItem {
  id: string;
  name: string;
}

interface StudentItem {
  id: string;
  name: string;
  email?: string;
}

interface AttendanceItem {
  studentId: string;
  status: "present" | "absent" | "absent_with_permission";
}

interface AttendanceRecord {
  id: string;
  studentId: string;
  status: string;
  date: string;
}

interface AttendanceModalProps {
  embedded?: boolean;
  onClose?: () => void;
  selectedClassId?: string;
  onSelectedClassChange?: (value: string) => void;
  selectedDate?: string;
  onSelectedDateChange?: (value: string) => void;
}

const AttendanceModal: React.FC<AttendanceModalProps> = ({
  embedded = false,
  onClose,
  selectedClassId,
  onSelectedClassChange,
  selectedDate,
  onSelectedDateChange
}) => {
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState("");
  const [students, setStudents] = useState<StudentItem[]>([]);
  const [attendance, setAttendance] = useState<AttendanceItem[]>([]);
  const [internalSelectedClass, setInternalSelectedClass] = useState("");
  const [internalDate, setInternalDate] = useState(new Date().toISOString().split('T')[0]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [bulkNotice, setBulkNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const initialAttendanceRef = useRef<AttendanceItem[]>([]);
  const lastBulkRef = useRef<AttendanceItem[] | null>(null);
  const location = useLocation();

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const userId = localStorage.getItem("userId");
  const requestedClassId = new URLSearchParams(location.search).get("classId") || "";
  const resolvedSelectedClass = selectedClassId ?? internalSelectedClass;
  const resolvedDate = selectedDate ?? internalDate;
  const setResolvedSelectedClass = onSelectedClassChange ?? setInternalSelectedClass;
  const setResolvedDate = onSelectedDateChange ?? setInternalDate;

  const fetchClasses = useCallback(async () => {
    try {
      if (!token) {
        setClasses([]);
        return;
      }
      setClassesLoading(true);
      setClassesError("");

      let res;
      if (role === "admin" || role === "superadmin" || role === "headteacher") {
        res = await axios.get(getApiUrl("academics/classes"), { headers: { Authorization: `Bearer ${token}` } });
        const nextClasses = Array.isArray(res.data) ? res.data : res.data.classes || [];
        setClasses(nextClasses);
        if (!resolvedSelectedClass) {
          const matchedRequestedClass = requestedClassId
            ? nextClasses.find((cls: any) => String(cls.id || cls._id) === requestedClassId)
            : null;
          if (matchedRequestedClass) {
            setResolvedSelectedClass(String(matchedRequestedClass.id || matchedRequestedClass._id));
          } else if (nextClasses.length > 0) {
            setResolvedSelectedClass(String(nextClasses[0].id || nextClasses[0]._id));
          }
        }
      } else if (role === "teacher") {
        try {
          res = await axios.get(getApiUrl("teacher/dashboard"), { headers: { Authorization: `Bearer ${token}` } });
          const nextClasses = res.data.classes || [];
          setClasses(nextClasses);
          if (!resolvedSelectedClass) {
            const matchedRequestedClass = requestedClassId
              ? nextClasses.find((cls: any) => String(cls.id || cls._id) === requestedClassId)
              : null;
            if (matchedRequestedClass) {
              setResolvedSelectedClass(String(matchedRequestedClass.id || matchedRequestedClass._id));
            } else if (nextClasses.length > 0) {
              setResolvedSelectedClass(String(nextClasses[0].id || nextClasses[0]._id));
            }
          }
        } catch {
          try {
            const fallbackRes = await axios.get(getApiUrl("academics/classes"), { headers: { Authorization: `Bearer ${token}` } });
            const data = Array.isArray(fallbackRes.data) ? fallbackRes.data : fallbackRes.data.classes || [];
            const filtered = userId
              ? data.filter((c: any) =>
                String(c.teacherId || c.teacher?.id || "") === String(userId) ||
                String(c.assistantTeacherId || c.assistantTeacher?.id || "") === String(userId)
              )
              : [];
            setClasses(filtered);
            if (!resolvedSelectedClass) {
              const matchedRequestedClass = requestedClassId
                ? filtered.find((cls: any) => String(cls.id || cls._id) === requestedClassId)
                : null;
              if (matchedRequestedClass) {
                setResolvedSelectedClass(String(matchedRequestedClass.id || matchedRequestedClass._id));
              } else if (filtered.length > 0) {
                setResolvedSelectedClass(String(filtered[0].id || filtered[0]._id));
              }
            }
          } catch (fallbackErr: any) {
            setClasses([]);
            setClassesError("Failed to load classes. Please try again.");
          }
        }
      } else {
        setClasses([]);
      }
    } catch {
      setClasses([]);
      setClassesError("Failed to load classes. Please try again.");
    } finally {
      setClassesLoading(false);
    }
  }, [token, role, userId, requestedClassId, resolvedSelectedClass, setResolvedSelectedClass]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    if (!classesError) return;
    const timeoutId = window.setTimeout(() => setClassesError(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [classesError]);

  const fetchData = useCallback(async () => {
    if (!resolvedSelectedClass || !resolvedDate) return;
    setLoading(true);
    setError("");
    try {
      let studentRes;
      if (role === "admin" || role === "superadmin" || role === "headteacher") {
        studentRes = await axios.get(getApiUrl(`academics/classes/${resolvedSelectedClass}/students`), {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        studentRes = await axios.get(getApiUrl(`teacher/classes/${resolvedSelectedClass}/students`), {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      const studentData = Array.isArray(studentRes.data) ? studentRes.data : studentRes.data.students || [];
      setStudents(studentData);

      try {
        const attendanceRes = await axios.get(getApiUrl(`attendance?classId=${resolvedSelectedClass}&date=${resolvedDate}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingRecords = attendanceRes.data.records || [];

        if (existingRecords.length > 0) {
          const mapped = studentData.map((s: StudentItem) => {
            const existing = existingRecords.find((r: any) => String(r.studentId) === String(s.id));
            return {
              studentId: String(s.id),
              status: (existing?.status || 'present') as any
            };
          });
          setAttendance(mapped);
          initialAttendanceRef.current = mapped;
        } else {
          const seeded = studentData.map((s: any) => ({ studentId: String(s.id), status: "present" }));
          setAttendance(seeded);
          initialAttendanceRef.current = seeded;
        }
      } catch {
        const seeded = studentData.map((s: any) => ({ studentId: String(s.id), status: "present" }));
        setAttendance(seeded);
        initialAttendanceRef.current = seeded;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load students. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [resolvedSelectedClass, resolvedDate, token, role]);

  useEffect(() => {
    if (resolvedSelectedClass) fetchData();
  }, [resolvedSelectedClass, resolvedDate, fetchData]);

  const updateAttendance = (studentId: string, status: any) => {
    const normalizedStudentId = String(studentId);
    setAttendance(prev => prev.map(a => String(a.studentId) === normalizedStudentId ? { ...a, status } : a));
  };

  const bulkUpdate = (status: any) => {
    lastBulkRef.current = attendance;
    setAttendance(prev => prev.map(a => ({ ...a, status })));
    const label = status === "present" ? "Present" : status === "absent" ? "Absent" : "Excused";
    setBulkNotice(`Marked all as ${label}.`);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      await axios.post(getApiUrl("attendance"), {
        classId: resolvedSelectedClass,
        date: resolvedDate,
        records: attendance,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage("Attendance records saved successfully.");
      initialAttendanceRef.current = attendance;
      await fetchData();
      setTimeout(() => {
        setMessage("");
        if (onClose) onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save attendance records. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Filter students by search term and status
  const filteredStudents = students.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.email || '').toLowerCase().includes(searchTerm.toLowerCase());

    if (statusFilter === "all") return matchesSearch;

    const studentAttendance = attendance.find(a => String(a.studentId) === String(s.id));
    const currentStatus = studentAttendance?.status || 'present';

    if (statusFilter === "present") return matchesSearch && currentStatus === 'present';
    if (statusFilter === "absent") return matchesSearch && currentStatus === 'absent';
    if (statusFilter === "excused") return matchesSearch && currentStatus === 'absent_with_permission';

    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedStudents = filteredStudents.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const stats = {
    present: attendance.filter(a => a.status === 'present').length,
    absent: attendance.filter(a => a.status === 'absent').length,
    excused: attendance.filter(a => a.status === 'absent_with_permission').length
  };
  const hasUnsavedChanges = attendance.length > 0 && attendance.some((item, idx) => {
    const initial = initialAttendanceRef.current[idx];
    return !initial || initial.studentId !== item.studentId || initial.status !== item.status;
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const content = (
    <>
      {!embedded && (
        <div className="utopia-modal-header bg-slate-50 p-8 border-b border-slate-100 shrink-0">
          <div>
            <h3 className="text-2xl font-black text-slate-800">Attendance Register</h3>
            <p className="text-sm text-slate-600 mt-2 font-medium">
              Record student attendance for selected class and date
            </p>
          </div>
          <button onClick={onClose} className="utopia-modal-close-btn" style={{ width: '40px', height: '40px' }}>
            <X size={20} />
          </button>
        </div>
      )}

      <div className="flex flex-col flex-1 overflow-hidden">
        {classesError && (
          <div className="mx-8 mt-6 mb-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            {classesError}
          </div>
        )}
        {/* Top Control Bar */}
        <div className="p-8 pb-6 border-b border-slate-100">
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-2 min-w-[150px]">
              <select
                className="utopia-input min-h-[44px] text-sm font-semibold bg-white px-3 py-2 leading-normal"
                value={resolvedSelectedClass}
                onChange={e => setResolvedSelectedClass(e.target.value)}
                disabled={classesLoading}
              >
                {!classesLoading && classes.length === 0 && (
                  <option value="" disabled>No classes available</option>
                )}
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-2 min-w-[140px]">
              <input type="date" className="utopia-input min-h-[44px] text-sm font-semibold bg-white leading-normal px-3 py-2" value={resolvedDate} onChange={e => setResolvedDate(e.target.value)} />
            </div>

            <div className="flex flex-col gap-2 min-w-[140px] max-w-[180px] w-full">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Student name or ID"
                  className="utopia-input pl-9 pr-9 h-11 text-sm bg-slate-50 border-2 border-slate-200 w-full font-semibold leading-none"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setSearchTerm("")}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 min-w-[130px]">
              <select
                className="utopia-input min-h-[44px] text-sm font-semibold bg-white border-2 border-slate-200 px-3 py-2 leading-normal"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="excused">Excused</option>
              </select>
            </div>

            <button
              onClick={() => bulkUpdate('present')}
              className="h-11 px-4 rounded-lg bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200 text-xs font-black uppercase transition-all tracking-widest border-2 border-slate-200"
            >
              Present All
            </button>
            <button
              onClick={() => bulkUpdate('absent')}
              className="h-11 px-4 rounded-lg bg-slate-100 text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 text-xs font-black uppercase transition-all tracking-widest border-2 border-slate-200"
            >
              Absent All
            </button>
            <button
              onClick={() => bulkUpdate('absent_with_permission')}
              className="h-11 px-4 rounded-lg bg-slate-100 text-slate-600 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 text-xs font-black uppercase transition-all tracking-widest border-2 border-slate-200"
            >
              Excused All
            </button>
            <button
              className="utopia-btn-primary h-11 px-5 text-sm font-bold flex items-center justify-center gap-2"
              onClick={handleSubmit}
              disabled={loading || !resolvedSelectedClass || students.length === 0}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save Attendance
            </button>
            {hasUnsavedChanges && (
              <span className="text-xs font-bold text-amber-600 uppercase tracking-wider">Unsaved changes</span>
            )}
          </div>
          {(message || error || bulkNotice) && (
            <div className="mt-4 space-y-3">
              {message && (
                <div className="p-4 bg-teal-50 border-2 border-teal-200 rounded-2xl text-teal-700 text-sm font-bold uppercase tracking-widest flex items-center gap-4">
                  <CheckCircle2 size={20} /> {message}
                </div>
              )}
              {error && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-2xl text-red-700 text-sm font-bold uppercase tracking-widest flex items-center gap-4">
                  <AlertCircle size={20} /> {error}
                </div>
              )}
              {bulkNotice && (
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-sm font-semibold flex items-center justify-between gap-4">
                  <span>{bulkNotice}</span>
                  {lastBulkRef.current && (
                    <button
                      className="text-xs font-black uppercase tracking-widest text-slate-600 hover:text-slate-800"
                      onClick={() => {
                        if (lastBulkRef.current) setAttendance(lastBulkRef.current);
                        setBulkNotice(null);
                      }}
                    >
                      Undo
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-8 pt-6">
              {loading && students.length === 0 ? (
                <div className="space-y-4">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div key={idx} className="h-16 rounded-xl bg-slate-50 animate-pulse" />
                  ))}
                </div>
              ) : paginatedStudents.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-4 px-4 text-sm font-black text-slate-700 uppercase tracking-wider">Student</th>
                          <th className="text-center py-4 px-4 text-sm font-black text-slate-700 uppercase tracking-wider">Present</th>
                          <th className="text-center py-4 px-4 text-sm font-black text-slate-700 uppercase tracking-wider">Absent</th>
                          <th className="text-center py-4 px-4 text-sm font-black text-slate-700 uppercase tracking-wider">Excused</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedStudents.map(s => {
                          const current = attendance.find(a => String(a.studentId) === String(s.id))?.status || 'present';
                          return (
                            <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 font-black text-base shadow-inner">
                                    {s.name.charAt(0)}
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-slate-800 text-base">{s.name}</h4>
                                    <p className="text-xs text-slate-500 font-mono tracking-wider mt-1">{String(s.id || '').slice(-8)}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <button
                                  onClick={() => updateAttendance(String(s.id), 'present')}
                                  className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all border-2 ${current === 'present' ? 'bg-teal-500 text-white border-teal-500 shadow-lg shadow-teal-200' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-300'}`}
                                  title="Mark as Present"
                                >
                                  Present
                                </button>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <button
                                  onClick={() => updateAttendance(String(s.id), 'absent')}
                                  className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all border-2 ${current === 'absent' ? 'bg-rose-500 text-white border-rose-500 shadow-lg shadow-rose-200' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-300'}`}
                                  title="Mark as Absent"
                                >
                                  Absent
                                </button>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <button
                                  onClick={() => updateAttendance(String(s.id), 'absent_with_permission')}
                                  className={`px-6 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-all border-2 ${current === 'absent_with_permission' ? 'bg-amber-500 text-white border-amber-500 shadow-lg shadow-amber-200' : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-300'}`}
                                  title="Mark as Excused"
                                >
                                  Excused
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between border-t border-slate-200 pt-6">
                      <div className="text-sm text-slate-700 font-bold">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-4 py-2 rounded-xl bg-white border-2 border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-semibold"
                        >
                          <ChevronLeft size={18} />
                          Previous
                        </button>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum: number;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`px-4 py-2 rounded-xl border-2 font-semibold transition-all ${currentPage === pageNum
                                  ? 'bg-teal-500 text-white border-teal-500'
                                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 rounded-xl bg-white border-2 border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-semibold"
                        >
                          Next
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : resolvedSelectedClass ? (
                <div className="py-40 text-center text-slate-300">
                  <User size={80} className="mx-auto mb-6 opacity-5" />
                  <p className="font-medium text-slate-400">No learners found for the selected class/date.</p>
                </div>
              ) : (
                <div className="py-40 text-center text-slate-300">
                  <Layers size={80} className="mx-auto mb-6 opacity-5" />
                  <p className="font-medium text-slate-400">Identify an academic cohort to synchronize records.</p>
                </div>
              )}
            </div>

            <div className="px-8 pb-8">
              <div className="flex items-center justify-end">
                <button
                  className="utopia-btn-primary h-11 px-6 text-sm font-bold flex items-center justify-center gap-2"
                  onClick={handleSubmit}
                  disabled={loading || !resolvedSelectedClass || students.length === 0}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Attendance
                </button>
              </div>
            </div>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="attendance-embedded">
        {content}
      </div>
    );
  }

  return (
    <div className="utopia-modal-overlay" style={{ zIndex: 9999, backgroundColor: 'rgba(0, 0, 0, 0.6)' }}>
      <div className="utopia-modal-content" style={{ maxWidth: '90vw', width: '1200px', height: '85vh', minHeight: '600px', maxHeight: '900px' }}>
        {content}
      </div>
    </div>
  );
};

export default AttendanceModal;
